import PropTypes from "prop-types";
import { useField } from "formik";
import IconButton from "@mui/material/IconButton";
import { memo, useCallback, useMemo } from "react";
import moment from "moment";
import InputAdornment from "@mui/material/InputAdornment";
import { useTranslation } from "react-i18next";
import CalendarIcon from "components/Icons/CalendarIcon";
import useBoolean from "hooks/useBoolean";
import DateTimePickers from "components/mui5/DateTimePickers";

const EndAdornment = ({ onClick }) => (
  <InputAdornment position="end">
    <IconButton onClick={onClick}>
      <CalendarIcon />
    </IconButton>
  </InputAdornment>
);

function CustomDateRange(props) {
  const { t } = useTranslation();

  const { value: pickerOpen, setFalse, toggle } = useBoolean();
  const [startInputField, startMeta, startHelpers] = useField(props.startInputName);
  const [endInputField, endMeta, endHelpers] = useField(props.endInputName);

  const multiInputDateRangeFieldProps = useMemo(() => {
    return {
      slots: { field: DateTimePickers.MultiInputDateRangeField },
      slotProps: {
        popper: { open: pickerOpen },
        field: {
          dateSeparator: t("global.to_preposition"),
          onBlur: () => {
            startHelpers.setTouched(true);
            endHelpers.setTouched(true);
          },
        },
        textField: ({ position }) => ({
          variant: "standard",
          size: "small",
          InputProps: {
            placeholder: "DD / MM / YYYY",
            endAdornment: <EndAdornment onClick={toggle} />,
          },
          error: Boolean(
            position === "start"
              ? Boolean(startMeta.touched && startMeta.error)
              : Boolean(endMeta.touched && endMeta.error)
          ),
          helperText:
            position === "start"
              ? Boolean(startMeta.touched && startMeta.error)
                ? t(startMeta.error)
                : undefined
              : Boolean(endMeta.touched && endMeta.error)
                ? t(endMeta.error)
                : undefined,
        }),
      },
    };
  }, [
    pickerOpen,
    t,
    startHelpers,
    endHelpers,
    toggle,
    startMeta.touched,
    startMeta.error,
    endMeta.touched,
    endMeta.error,
  ]);

  const handleUpdate = useCallback(
    ([startDate, endDate]) => {
      startHelpers.setValue(moment(startDate));
      endHelpers.setValue(moment(endDate));
    },
    [endHelpers, startHelpers]
  );

  const dateRangeValues = useMemo(
    () => [moment(startInputField.value), moment(endInputField.value)],
    [endInputField.value, startInputField.value]
  );

  return (
    <DateTimePickers.DateRangePicker
      disableFuture
      value={dateRangeValues}
      onChange={handleUpdate}
      onClose={setFalse}
      slots={multiInputDateRangeFieldProps.slots}
      slotProps={multiInputDateRangeFieldProps.slotProps}
    />
  );
}

CustomDateRange.propTypes = {
  startInputName: PropTypes.string.isRequired,
  endInputName: PropTypes.string.isRequired,
};

export default memo(CustomDateRange);
